import type { MigrateTeamToSupportV2Step } from "@redotech/admin-sdk/rpc/schema/support/migrate-team-to-support-v2";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

const STEP_OPTIONS: readonly {
  readonly value: MigrateTeamToSupportV2Step;
  readonly label: string;
}[] = [
  { value: "INTEGRATIONS", label: "Integrations" },
  { value: "TAGS", label: "Tags" },
  { value: "CONVERSATIONS", label: "Conversations" },
  { value: "SETTINGS_BACKFILL", label: "Settings" },
  { value: "RULES_V2_TO_V3", label: "Rules" },
];

// Conversations depend on integrations + tags being migrated first. When
// the operator selects conversations we force-include these so they
// re-run as needed on a cold team.
const CONVERSATIONS_DEPENDENCIES: readonly MigrateTeamToSupportV2Step[] = [
  "INTEGRATIONS",
  "TAGS",
];

const ALL_STEPS: readonly MigrateTeamToSupportV2Step[] = STEP_OPTIONS.map(
  (o) => o.value,
);

export const MigrateToSupportV2Card = memo(function MigrateToSupportV2Card({
  teamId,
}: {
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [pending, setPending] = useState(false);
  const [workflowId, setWorkflowId] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [expanded, setExpanded] = useState(false);
  const [selectedSteps, setSelectedSteps] = useState<
    ReadonlySet<MigrateTeamToSupportV2Step>
  >(() => new Set(ALL_STEPS));

  // Integrations and tags are forced on (and non-editable) while
  // conversations is selected.
  const forcedByConversations = selectedSteps.has("CONVERSATIONS");
  const isForced = (step: MigrateTeamToSupportV2Step) =>
    forcedByConversations && CONVERSATIONS_DEPENDENCIES.includes(step);

  const toggleStep = useHandler((step: MigrateTeamToSupportV2Step) => {
    setSelectedSteps((prev) => {
      if (prev.has(step) && isForced(step)) return prev;
      const next = new Set(prev);
      if (next.has(step)) {
        next.delete(step);
      } else {
        next.add(step);
        // Checking conversations pulls in its dependencies automatically.
        if (step === "CONVERSATIONS") {
          for (const dep of CONVERSATIONS_DEPENDENCIES) next.add(dep);
        }
      }
      return next;
    });
  });

  const stepsToSend = useMemo<MigrateTeamToSupportV2Step[] | undefined>(() => {
    // All-selected is semantically equivalent to "run everything"; send
    // undefined so the workflow id stays stable for the full-run case.
    if (selectedSteps.size === ALL_STEPS.length) return undefined;
    return STEP_OPTIONS.filter((o) => selectedSteps.has(o.value)).map(
      (o) => o.value,
    );
  }, [selectedSteps]);

  const runAll = stepsToSend === undefined;

  const handleMigrate = useHandler(async () => {
    setPending(true);
    setError(undefined);
    setWorkflowId(undefined);
    try {
      const result = await rpcClient.migrateTeamToSupportV2({
        teamId,
        steps: stepsToSend,
      });
      setWorkflowId(result.workflowId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
    }
  });

  return (
    <Card radius="sm" title="Migrate team to support-v2">
      <Flex dir="column" gap="md" w="full">
        <Text fontSize="sm">
          Kicks off the per-team support-v2 migration Temporal workflow.
        </Text>

        <Flex
          align="center"
          gap="xs"
          onClick={() => setExpanded((v) => !v)}
          style={{ cursor: "pointer" }}
        >
          <RedoIcon
            Icon={expanded ? ChevronDownIcon : ChevronRightIcon}
            size={16}
          />
          <Text fontSize="sm" fontWeight="medium">
            Steps ({selectedSteps.size}/{ALL_STEPS.length} selected)
          </Text>
        </Flex>

        {expanded && (
          <Flex dir="column" gap="xs" pl="md">
            {STEP_OPTIONS.map((option) => (
              <RedoCheckbox
                disabled={isForced(option.value)}
                key={option.value}
                label={option.label}
                setValue={() => toggleStep(option.value)}
                size="sm"
                value={selectedSteps.has(option.value)}
              />
            ))}
          </Flex>
        )}

        <Flex align="center" gap="md">
          <RedoButton
            disabled={pending || selectedSteps.size === 0}
            hierarchy="primary"
            onClick={handleMigrate}
            pending={pending}
            text={runAll ? "Run full migration" : "Run selected steps"}
          />
          {workflowId && (
            <Text fontSize="sm" fontWeight="medium">
              Started: {workflowId}
            </Text>
          )}
          {error && (
            <Text fontSize="sm" fontWeight="medium" textColor="error">
              Error: {error}
            </Text>
          )}
        </Flex>
      </Flex>
    </Card>
  );
});
